import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useLocalSettings } from '@/lib/contexts/LocalSettingsContext';
import type { ExampleCardLayout } from '@/components/demand/ExampleCard';
const STORAGE_KEY = 'demandFeed.layout';
const DEFAULT_LAYOUT: ExampleCardLayout = 'side-by-side';
function isLayout(value: unknown): value is ExampleCardLayout {
  return value === 'side-by-side' || value === 'stacked';
}

/**
 * Reactive layout preference for the inspiration feed, persisted via
 * LocalSettings (localStorage). Initializes to the stored value on mount to
 * avoid a hydration mismatch, then keeps state and storage in sync.
 */
export function useDemandFeedLayout(t0) {
  const $ = _c(12);
  const defaultLayout = t0 === undefined ? DEFAULT_LAYOUT : t0;
  const settings = useLocalSettings();
  const [layout, setLayoutState] = useState(defaultLayout);
  let t1;
  let t2;
  if ($[0] !== settings) {
    t1 = () => {
      const stored = settings.get(STORAGE_KEY);
      if (isLayout(stored)) {
        setLayoutState(stored);
      }
    };
    t2 = [settings];
    $[0] = settings;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== settings) {
    t3 = next => {
      setLayoutState(next);
      settings.set(STORAGE_KEY, next);
    };
    $[3] = settings;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const setLayout = t3;
  let t4;
  if ($[5] !== layout || $[6] !== setLayout) {
    t4 = () => {
      setLayout(layout === "side-by-side" ? "stacked" : "side-by-side");
    };
    $[5] = layout;
    $[6] = setLayout;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const toggleLayout = t4;
  let t5;
  if ($[8] !== layout || $[9] !== setLayout || $[10] !== toggleLayout) {
    t5 = {
      layout,
      setLayout,
      toggleLayout
    };
    $[8] = layout;
    $[9] = setLayout;
    $[10] = toggleLayout;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}